import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import SEOHead from '../components/SEOHead';
import BatchImageCompressor from '../components/BatchImageCompressor';

const { 
  FiLayers, FiZap, FiShield, FiDownload, FiSettings, FiImage, 
  FiStar, FiUsers, FiTrendingUp, FiClock, FiCheckCircle 
} = FiIcons;

function BatchCompress() {
  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const features = [
    {
      icon: FiLayers,
      title: "Bulk Compression",
      description: "Compress hundreds of images simultaneously with our advanced batch processing engine"
    },
    {
      icon: FiZap,
      title: "Lightning Fast",
      description: "Optimized algorithms ensure rapid compression without compromising image quality"
    },
    {
      icon: FiShield,
      title: "100% Secure",
      description: "All processing happens locally in your browser - your images never leave your device"
    },
    {
      icon: FiSettings,
      title: "Advanced Settings",
      description: "Customize quality, format, and compression levels for all images at once"
    },
    {
      icon: FiDownload,
      title: "Batch Download",
      description: "Download all compressed images at once in a ZIP file or individually"
    },
    {
      icon: FiImage,
      title: "Multiple Formats",
      description: "Support for JPEG, PNG, WebP with format conversion options"
    }
  ];

  const benefits = [
    {
      icon: FiClock,
      title: "Save Time",
      description: "Compress hundreds of images in minutes instead of hours of manual work"
    },
    {
      icon: FiTrendingUp,
      title: "Boost Performance",
      description: "Optimize entire image libraries for faster websites and applications"
    },
    {
      icon: FiCheckCircle,
      title: "Consistent Results",
      description: "Apply the same compression settings across all images for uniform optimization"
    }
  ];

  const useCases = [
    {
      title: "Website Optimization",
      description: "Compress all website images for faster loading times and better SEO",
      icon: "🌐"
    },
    {
      title: "E-commerce Products",
      description: "Optimize product image catalogs while maintaining visual quality",
      icon: "🛍️"
    },
    {
      title: "Photography Portfolio",
      description: "Create web-optimized versions of high-resolution photo collections",
      icon: "📸"
    },
    {
      title: "Mobile App Assets",
      description: "Reduce app size by compressing all image assets efficiently",
      icon: "📱"
    },
    {
      title: "Email Campaigns",
      description: "Optimize images for email marketing with size constraints",
      icon: "📧"
    },
    {
      title: "Cloud Storage",
      description: "Save storage space by compressing photo libraries and archives",
      icon: "☁️"
    }
  ];

  return (
    <>
      <SEOHead
        title="Free Batch Image Compressor - Compress Multiple Images Online"
        description="Batch compress hundreds of images at once for free! Professional bulk image compression with ZIP download, custom quality settings, and secure processing. Perfect for websites, e-commerce, and photo libraries."
        keywords="batch image compressor, bulk image compression, compress multiple images, bulk photo compressor, batch image optimization, multiple image compressor, bulk JPEG compressor, batch PNG compression, website image optimization, bulk photo reducer"
        url="/batch-compress"
        image="https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=1200&h=630&fit=crop"
      />
      
      <div className="min-h-screen">
        {/* Hero Section */}
        <section className="relative py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-green-50 to-emerald-100">
          <div className="max-w-7xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-center mb-16"
            >
              <div className="bg-green-600 w-20 h-20 rounded-2xl flex items-center justify-center mx-auto mb-6">
                <SafeIcon icon={FiLayers} className="text-white text-3xl" />
              </div>
              <h1 className="text-5xl md:text-6xl font-bold text-gray-900 mb-6">
                Free Batch Image Compressor
                <span className="block text-green-600">Compress Multiple Images</span>
              </h1>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto mb-8">
                Compress hundreds of images at once with our professional batch processing tool. 
                Reduce file sizes by up to 90% while maintaining quality - completely free and secure.
              </p>
              
              {/* Quick Stats */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-8 max-w-2xl mx-auto">
                {[
                  { number: "100+", label: "Images Per Batch" },
                  { number: "90%", label: "Size Reduction" },
                  { number: "3+", label: "Output Formats" },
                  { number: "0$", label: "Cost Forever" }
                ].map((stat, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.1 }}
                    className="text-center"
                  >
                    <div className="text-3xl font-bold text-green-600">{stat.number}</div>
                    <div className="text-sm text-gray-600">{stat.label}</div>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </div>
        </section>

        {/* Batch Compressor Tool */}
        <section className="py-16 px-4 sm:px-6 lg:px-8">
          <BatchImageCompressor />
        </section>

        {/* Features Section */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-16"
            >
              <h2 className="text-4xl font-bold text-gray-900 mb-4">
                Powerful Batch Compression Features
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Everything you need for professional bulk image compression
              </p>
            </motion.div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {features.map((feature, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="bg-gray-50 rounded-2xl p-8 hover:shadow-lg transition-all duration-300"
                >
                  <div className="bg-green-100 w-12 h-12 rounded-lg flex items-center justify-center mb-4">
                    <SafeIcon icon={feature.icon} className="text-green-600 text-xl" />
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-3">
                    {feature.title}
                  </h3>
                  <p className="text-gray-600">
                    {feature.description}
                  </p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Benefits Section */}
        <section className="py-20 bg-gradient-to-br from-blue-50 to-indigo-100">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-16"
            >
              <h2 className="text-4xl font-bold text-gray-900 mb-4">
                Why Choose Batch Compression?
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Transform your image workflow with professional batch processing
              </p>
            </motion.div>

            <div className="grid md:grid-cols-3 gap-8">
              {benefits.map((benefit, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.2 }}
                  className="text-center bg-white rounded-2xl p-8 shadow-lg"
                >
                  <div className="bg-blue-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6">
                    <SafeIcon icon={benefit.icon} className="text-blue-600 text-2xl" />
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-4">
                    {benefit.title}
                  </h3>
                  <p className="text-gray-600">
                    {benefit.description}
                  </p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Use Cases Section */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-16"
            >
              <h2 className="text-4xl font-bold text-gray-900 mb-4">
                Perfect for Every Use Case
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                From web optimization to storage management, our batch compressor handles it all
              </p>
            </motion.div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {useCases.map((useCase, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="bg-gray-50 rounded-2xl p-6 hover:shadow-lg transition-all duration-300"
                >
                  <div className="text-4xl mb-4">{useCase.icon}</div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">
                    {useCase.title}
                  </h3>
                  <p className="text-gray-600">
                    {useCase.description}
                  </p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* How It Works Section */}
        <section className="py-20 bg-gradient-to-br from-gray-50 to-gray-100">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-16"
            >
              <h2 className="text-4xl font-bold text-gray-900 mb-4">
                How Batch Compression Works
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Compress multiple images in just four simple steps
              </p>
            </motion.div>

            <div className="grid md:grid-cols-4 gap-8">
              {[
                {
                  step: "1",
                  title: "Select Multiple Images",
                  description: "Drag and drop or select multiple images from your device. Support for all major formats."
                },
                {
                  step: "2",
                  title: "Configure Settings",
                  description: "Set quality level, output format, and download options for all images at once."
                },
                {
                  step: "3",
                  title: "Process Batch",
                  description: "Click 'Compress All' and watch as all your images are processed simultaneously."
                },
                {
                  step: "4",
                  title: "Download Results",
                  description: "Download all compressed images as a ZIP file or individually as needed."
                }
              ].map((step, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.2 }}
                  className="text-center"
                >
                  <div className="bg-green-600 text-white w-16 h-16 rounded-full flex items-center justify-center text-2xl font-bold mx-auto mb-6">
                    {step.step}
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-4">
                    {step.title}
                  </h3>
                  <p className="text-gray-600">
                    {step.description}
                  </p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-20 bg-white">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-16"
            >
              <h2 className="text-4xl font-bold text-gray-900 mb-4">
                Batch Compression FAQ
              </h2>
            </motion.div>

            <div className="space-y-8">
              {[
                {
                  question: "How many images can I compress at once?",
                  answer: "You can compress up to 100 images simultaneously. For optimal performance, we recommend batches of 50 images or fewer, especially for large files."
                },
                {
                  question: "What's the maximum file size for batch compression?",
                  answer: "Each image can be up to 50MB in size. The total batch size is limited by your device's memory, but our tool is optimized to handle large batches efficiently."
                },
                {
                  question: "Can I apply different compression settings to different images?",
                  answer: "Currently, batch processing applies the same settings to all images in the batch. For different settings, you can process images in separate batches or use our single image compressor."
                },
                {
                  question: "How long does batch compression take?",
                  answer: "Processing time depends on the number of images, their size, and your device's performance. Typically, 50 medium-sized images compress in under 3 minutes."
                },
                {
                  question: "Are my images stored on your servers during batch processing?",
                  answer: "No! All batch processing happens locally in your browser. Your images never leave your device, ensuring complete privacy and security."
                },
                {
                  question: "Can I pause or cancel batch processing?",
                  answer: "Yes, you can stop the processing at any time. Already processed images will remain available for download, and you can remove unprocessed images from the queue."
                }
              ].map((faq, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="bg-gray-50 rounded-lg p-6"
                >
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">
                    {faq.question}
                  </h3>
                  <p className="text-gray-600">
                    {faq.answer}
                  </p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 bg-green-600">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="text-4xl font-bold text-white mb-4">
                Ready to Compress Your Images?
              </h2>
              <p className="text-xl text-green-100 mb-8">
                Start batch compressing your images now - it's fast, free, and secure!
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                  className="bg-white text-green-600 px-8 py-4 rounded-lg font-semibold text-lg hover:bg-gray-50 transition-colors"
                >
                  Start Batch Compression
                </motion.button>
                <Link to="/compress">
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    className="bg-green-700 text-white px-8 py-4 rounded-lg font-semibold text-lg hover:bg-green-800 transition-colors"
                  >
                    Try Single Image Compressor
                  </motion.button>
                </Link>
              </div>
            </motion.div>
          </div>
        </section>
      </div>
    </>
  );
}

export default BatchCompress;